/**
 * Application form types - aligned with backend API endpoints
 */

import {
  DomainApplicationStatus,
  DomainAppMilestoneType,
  type DomainAddressType,
  type DomainAssetType,
  type DomainBorrowerEmploymentStatus,
  type DomainCitizenshipType,
  type DomainDownPaymentSourceType,
  type DomainExpenseType,
  type DomainFrequencyType,
  type DomainHomeSectionType,
  type DomainHomeUsageType,
  type DomainHousingType,
  type DomainIncomeType,
  type DomainLandSituationType,
  type DomainLeaseType,
  type DomainMaritalStatusType,
} from "@porchpass/api-client";
import type { DtoPorchpassBlockReason } from "src/utils/porchpassBlockReasons";

/** Represents a form field that can be empty (not yet selected) */
export type WithEmpty<T extends string> = T | "";

// ============================================================================
// Common Types
// ============================================================================

export interface Address {
  id?: string;
  serverId?: string;
  street: string;
  unit?: string;
  city: string;
  state: string;
  zipCode: string;
  county?: string;
  type: DomainAddressType;
  isMailingAddress: boolean;
  residedFromMonth?: string;
  residedFromYear?: string;
  residedToMonth?: string;
  residedToYear?: string;
  housingType?: DomainHousingType;
  housingTypeOther?: string;
  monthlyPayment?: number;
}

export interface EmploymentIncome {
  id?: string;
  serverId?: string;
  incomeType: DomainIncomeType;
  amount?: number;
  frequency: DomainFrequencyType;
  weeklyHours?: number;
  overtimeHours?: number;
}

export interface Employment {
  id?: string;
  serverId?: string;
  employerName: string;
  jobTitle: string;
  employerAddress?: Address;
  startMonth?: string;
  startYear?: string;
  endMonth?: string;
  endYear?: string;
  isCurrent: boolean;
  employmentType: DomainBorrowerEmploymentStatus;
  isSelfEmployed?: boolean;
  incomes: EmploymentIncome[];
}

export interface FinancialAccount {
  id?: string;
  serverId?: string;
  accountType: DomainAssetType;
  institutionName: string;
  accountNumber?: string;
  balance?: number;
}

export interface AdditionalIncome {
  id?: string;
  serverId?: string;
  type: DomainIncomeType;
  monthlyAmount?: number;
  typeOther?: string;
  startMonth?: string;
  startYear?: string;
  endMonth?: string;
  endYear?: string;
  isOngoing?: boolean;
  childrenAges?: number[];
}

export interface Expense {
  id?: string;
  serverId?: string;
  type: DomainExpenseType;
  monthlyAmount?: number;
  typeOther?: string;
  startMonth?: string;
  startYear?: string;
  endMonth?: string;
  endYear?: string;
  isOngoing?: boolean;
  childrenAges?: number[];
}

// ============================================================================
// Borrower Data (Maps to borrower_handler.go / BorrowerUpdateReq)
// ============================================================================

export interface BorrowerData {
  // Personal Information
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  email: string;
  phone: string;
  citizenship: WithEmpty<DomainCitizenshipType>;

  // PII
  dateOfBirth?: string;
  ssn?: string;
  hasBankruptcy?: boolean;
  bankruptcyDate?: string; // ISO date YYYY-MM-DD when hasBankruptcy === true

  // Marital Status
  maritalStatus: WithEmpty<DomainMaritalStatusType>;
  dependentsAgesYears?: number[];

  // ECOA / HMDA demographic data
  ethnicities?: Array<{ type: string; other?: string }>; // Maps to DtoEthnicity[]
  races?: Array<{ type: string; other?: string }>; // Maps to DtoRace[]
  biologicalSex?: string; // Maps to DomainBiologicalSexType

  // Address history
  hasSufficientAddressHistory: boolean;

  // Nearest relative (optional)
  nearestRelativeName?: string;
  nearestRelativeRelationship?: string;
  nearestRelativePhone?: string;

  // Employment history
  employmentStatus?: DomainBorrowerEmploymentStatus;
  hasSufficientEmploymentHistory: boolean;

  // Related data
  addresses: Address[];
  employmentHistory: Employment[];
  financialAccounts: FinancialAccount[];
  additionalIncome: AdditionalIncome[];
  expenses: Expense[];
}

// ============================================================================
// Home Data (Maps to home_handler.go / UpdateHomeReq)
// ============================================================================

export interface HomeData {
  // Home Details
  manufacturer?: string;
  manufacturerOther?: string;
  modelName?: string;
  modelYear?: number;
  serialNumber?: string;
  squareFootage?: number;
  numberOfBedrooms?: number;
  numberOfBathrooms?: number;
  homeType: WithEmpty<DomainHomeSectionType>;
  homeTypeOther?: string;
  isNew: boolean;

  // Primary Use
  primaryUse: WithEmpty<DomainHomeUsageType>;
  usageTypeOther?: string;
  occupancyType: "owner-occupied" | "renter-occupied" | "vacant";
}

// ============================================================================
// Land Data (Maps to land_handler.go / various land situation endpoints)
// ============================================================================

export interface LandData {
  landSituation: WithEmpty<DomainLandSituationType>;
  propertyAddress?: Address;

  // Shared across multiple situations
  estimatedLandValue?: number;
  acreage?: number;
  hasFrontagePublicRoad?: boolean;
  hasPavedRoad?: boolean;
  hoaFeeAmount?: number;
  purchaseDateMonth?: string;
  purchaseDateYear?: string;
  useAsDownPayment?: boolean;
  downPaymentAmount?: number;

  // Currently Financed specific
  monthlyLandPayment?: number;
  existingMortgageBalance?: number;
  isRefinance?: boolean;

  // Purchasing / Leasing shared
  landlordName?: string;

  // Leasing specific
  leaseType?: DomainLeaseType;
  monthlyLeasePayment?: number;
  hasPriceIncrease?: boolean;
  priceIncreaseExplanation?: string;

  /** From GET land when loan type is resolved; cleared on in-progress edits. */
  supportedByPorchpass?: boolean;
  /** From GET land when porchpass eligibility is resolved; cleared on in-progress edits. */
  blockReasons?: DtoPorchpassBlockReason[];
}

// ============================================================================
// Pricing Data (Maps to application_handler.go / UpdateApplicationReq)
// ============================================================================

export interface PricingData {
  // Loan Amount
  homePrice: number;
  downPaymentAmount: number;
  downPaymentPercentage: number;
  downPaymentSource?: WithEmpty<DomainDownPaymentSourceType>;
  downPaymentSourceOther?: string;
  loanAmount: number;

  // Additional Costs (sent with home update)
  upgradesCost: number;
  siteImprovementCost: number;
  yearlyPremium: number;

  // Land payoff (from summary endpoint, read-only)
  landPurchaseOrPayoffAmount?: number;

  // Loan Terms
  loanTerm: 15 | 20 | 25 | 30 | 0;
  interestRate?: number;

  // Monthly Payment Breakdown
  estimatedMonthlyPayment?: number;
  principalAndInterest?: number;
  propertyTaxes?: number;
  insurance?: number;

  // Additional Costs
  closingCosts?: number;
  prepaidItems?: number;
}

// ============================================================================
// Lender Configuration (Maps to lender_configuration_handler.go)
// ============================================================================

export interface LenderConfig {
  lenderId: string;
  lenderName: string;
  logoUrl?: string;
  enabled: boolean; // selected for this application
  autoSubmit?: boolean; // auto-submit to this lender when application is ready
  isPrimary?: boolean; // is the dealer's primary lender (always selected, cannot uncheck)
  dealerEnabled?: boolean; // is enabled in dealer settings (non-dealer-enabled lenders are hidden)
  priority?: number;
}

// ============================================================================
// Signature Data
// ============================================================================

export type SignatureStatus =
  | "not-ready"
  | "ready"
  | "preparing" // Envelope being created, before DocuSign modal opens
  | "validating" // After DocuSign modal closes, waiting for backend SIGNED confirmation
  | "signed"
  | "declined";

/** Check if a signature status indicates the application should be locked for editing */
export function isSignatureLocked(status?: SignatureStatus): boolean {
  return status === "preparing" || status === "validating";
}

/** Milestones only reached once the backend has finished processing signing completion */
const POST_SIGNING_MILESTONES: ReadonlySet<string> = new Set([
  DomainAppMilestoneType.AppMilestoneReadyToSubmit,
  DomainAppMilestoneType.AppMilestoneSubmitted,
  DomainAppMilestoneType.AppMilestoneSubmissionFailed,
  DomainAppMilestoneType.AppMilestonePreApproved,
  DomainAppMilestoneType.AppMilestoneClearToClose,
  DomainAppMilestoneType.AppMilestoneClosed,
  DomainAppMilestoneType.AppMilestoneFunded,
  DomainAppMilestoneType.AppMilestoneDeclined,
]);

/** Application statuses where a DocuSign envelope is still live for the application */
const SIGNING_APPLICATION_STATUSES: ReadonlySet<string> = new Set([
  DomainApplicationStatus.ApplicationSentForSignature,
  DomainApplicationStatus.ApplicationWaitingOnESigSvc,
]);

const getBorrowerSignatureStatuses = (
  signature: SignatureData,
  hasCoBorrower: boolean,
): (SignatureStatus | undefined)[] =>
  hasCoBorrower
    ? [signature.borrowerStatus, signature.coBorrowerStatus]
    : [signature.borrowerStatus];

/** Check if any borrower's signature has been confirmed by the backend (SIGNED) */
export function hasSignedBorrower(
  signature: SignatureData,
  hasCoBorrower: boolean,
): boolean {
  return getBorrowerSignatureStatuses(signature, hasCoBorrower).some(
    (status) => status === "signed",
  );
}

/**
 * Check if an application should be locked for editing.
 * A signed borrower always locks the application — the signature covers a
 * specific set of application data, so edits are only allowed once the
 * Reopen flow has reset every borrower out of SIGNED.
 */
export function isApplicationLockedForSigning(
  signature: SignatureData,
  hasCoBorrower: boolean,
): boolean {
  return (
    signature.applicationStatus ===
      DomainApplicationStatus.ApplicationSentForSignature ||
    hasSignedBorrower(signature, hasCoBorrower)
  );
}

/**
 * Check if the backend is still processing a signing (DEA-946).
 * Reopen for Edit must wait until DocuSign's webhooks have been handled.
 * A borrower who hasn't signed (or declined) with nobody validating means no
 * webhook is pending, so reopening safely voids the envelope.
 */
export function isSigningCompletionPending(
  signature: SignatureData,
  hasCoBorrower: boolean,
): boolean {
  const signingInProgress =
    SIGNING_APPLICATION_STATUSES.has(signature.applicationStatus ?? "") &&
    !POST_SIGNING_MILESTONES.has(signature.appMilestone ?? "");
  if (!signingInProgress) return false;

  const statuses = getBorrowerSignatureStatuses(signature, hasCoBorrower);

  // A borrower finished signing in DocuSign but the recipient-completed webhook
  // hasn't confirmed it. Reopening now lets the late webhook flip the reset
  // borrower back to SIGNED.
  const awaitingSignerConfirmation = statuses.some(
    (status) => status === "validating",
  );

  // Every borrower is confirmed SIGNED but the envelope-completed webhook hasn't
  // been processed (milestone not advanced). Reopening now deactivates the
  // envelope and the completion is skipped.
  const awaitingEnvelopeCompletion = statuses.every(
    (status) => status === "signed",
  );

  return awaitingSignerConfirmation || awaitingEnvelopeCompletion;
}

export interface SignatureData {
  borrowerStatus: SignatureStatus;
  borrowerSignedAt?: string;
  coBorrowerStatus?: SignatureStatus;
  coBorrowerSignedAt?: string;
  applicationStatus?: string;
  appMilestone?: string;
  envelopeId?: string;
}

// ============================================================================
// Lender Submission Data
// ============================================================================

export type LenderSubmissionStatus = "pending" | "submitted" | "auto-submitted";

export interface LenderSubmission extends LenderConfig {
  submissionStatus: LenderSubmissionStatus;
  submittedAt?: string;
}

// ============================================================================
// Complete Application Form Data
// ============================================================================

export interface ApplicationFormData {
  // Application metadata
  applicationId?: string;
  primaryBorrowerId?: string;
  coBorrowerId?: string;
  status: "draft" | "in-progress" | "submitted" | "approved" | "denied";
  salesRepName?: string;
  createdAt?: string;
  updatedAt?: string;

  // Borrower data
  primaryBorrower: BorrowerData;
  coBorrower?: BorrowerData;
  hasCoBorrower: boolean;

  // Property data
  home: HomeData;
  land: LandData;

  // Loan details
  pricing: PricingData;

  // Lender preferences
  lenders: LenderConfig[];

  // Signature
  signature: SignatureData;
}

// ============================================================================
// Form Step Types
// ============================================================================

export type ApplicationStep =
  | "summary"
  | "primary-borrower"
  | "co-borrower"
  | "property"
  | "pricing"
  | "lenders"
  | "signature";

export interface StepConfig {
  id: ApplicationStep;
  label: string;
  description?: string;
  completed: boolean;
  progress: number;
  disabled: boolean;
}

// ============================================================================
// Initial/Default Values
// ============================================================================

export const createEmptyBorrower = (): BorrowerData => ({
  firstName: "",
  middleName: "",
  lastName: "",
  suffix: "",
  email: "",
  phone: "",
  citizenship: "",
  dateOfBirth: "",
  ssn: "",
  maritalStatus: "",
  dependentsAgesYears: [],
  ethnicities: undefined,
  races: undefined,
  biologicalSex: "",
  hasSufficientAddressHistory: false,
  nearestRelativeName: "",
  nearestRelativeRelationship: "",
  nearestRelativePhone: "",
  hasSufficientEmploymentHistory: false,
  addresses: [],
  employmentHistory: [],
  financialAccounts: [],
  additionalIncome: [],
  expenses: [],
});

export const createEmptyHome = (): HomeData => ({
  manufacturer: "",
  manufacturerOther: undefined,
  modelName: "",
  modelYear: undefined,
  serialNumber: "",
  squareFootage: undefined,
  numberOfBedrooms: undefined,
  numberOfBathrooms: undefined,
  homeType: "",
  homeTypeOther: undefined,
  isNew: true,
  primaryUse: "",
  usageTypeOther: undefined,
  occupancyType: "owner-occupied",
});

export const createEmptyLand = (): LandData => ({
  landSituation: "",
  propertyAddress: undefined,
  estimatedLandValue: undefined,
  acreage: undefined,
  hasFrontagePublicRoad: undefined,
  hasPavedRoad: undefined,
  hoaFeeAmount: undefined,
  purchaseDateMonth: undefined,
  purchaseDateYear: undefined,
  useAsDownPayment: undefined,
  downPaymentAmount: undefined,
  monthlyLandPayment: undefined,
  existingMortgageBalance: undefined,
  isRefinance: undefined,
  landlordName: undefined,
  leaseType: undefined,
  monthlyLeasePayment: undefined,
  priceIncreaseExplanation: undefined,
});

export const createEmptyPricing = (): PricingData => ({
  homePrice: 0,
  downPaymentAmount: 0,
  downPaymentPercentage: 10,
  downPaymentSource: "",
  downPaymentSourceOther: "",
  loanAmount: 0,
  loanTerm: 0,
  upgradesCost: 0,
  siteImprovementCost: 0,
  yearlyPremium: 0,
});

export const createEmptySignature = (): SignatureData => ({
  borrowerStatus: "not-ready",
  borrowerSignedAt: undefined,
  coBorrowerStatus: undefined,
  coBorrowerSignedAt: undefined,
  appMilestone: undefined,
  envelopeId: undefined,
});

export const createEmptyApplication = (): ApplicationFormData => ({
  applicationId: undefined,
  status: "draft",
  primaryBorrower: createEmptyBorrower(),
  coBorrower: undefined,
  hasCoBorrower: false,
  home: createEmptyHome(),
  land: createEmptyLand(),
  pricing: createEmptyPricing(),
  lenders: [],
  signature: createEmptySignature(),
});
